

import { useState, useRef, useEffect } from "react"
import { PlayIcon, PauseIcon, SkipBackIcon, SkipForwardIcon, Volume2Icon, VolumeXIcon } from "lucide-react"

const AudioPlayer = ({ file, fileName }) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [volume, setVolume] = useState(0.7)
  const [isMuted, setIsMuted] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  const audioRef = useRef(null)
  const progressBarRef = useRef(null)

  useEffect(() => {
    // Reset state when file changes
    setIsPlaying(false)
    setCurrentTime(0)
    setIsLoading(true)
    setError(null)

    if (audioRef.current) {
      audioRef.current.load()
    }
  }, [file])

  const handleLoadedData = () => {
    setDuration(audioRef.current.duration)
    setIsLoading(false)
  }

  const handleLoadError = () => {
    setIsLoading(false)
    setError("Failed to load audio file")
  }

  const handlePlayPause = () => {
    if (isPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play()
    }
    setIsPlaying(!isPlaying)
  }

  const handleTimeUpdate = () => {
    setCurrentTime(audioRef.current.currentTime)
  }

  const handleProgressChange = (e) => {
    const newTime = e.target.value
    setCurrentTime(newTime)
    audioRef.current.currentTime = newTime
  }

  const handleVolumeChange = (e) => {
    const newVolume = Number.parseFloat(e.target.value)
    setVolume(newVolume)
    audioRef.current.volume = newVolume

    if (newVolume === 0) {
      setIsMuted(true)
    } else {
      setIsMuted(false)
    }
  }

  const toggleMute = () => {
    if (isMuted) {
      audioRef.current.volume = volume
      setIsMuted(false)
    } else {
      audioRef.current.volume = 0
      setIsMuted(true)
    }
  }

  const skipBackward = () => {
    audioRef.current.currentTime = Math.max(audioRef.current.currentTime - 10, 0)
  }

  const skipForward = () => {
    audioRef.current.currentTime = Math.min(audioRef.current.currentTime + 10, duration)
  }

  const formatTime = (time) => {
    if (isNaN(time)) return "0:00"

    const minutes = Math.floor(time / 60)
    const seconds = Math.floor(time % 60)
    return `${minutes}:${seconds.toString().padStart(2, "0")}`
  }

  return (
    <div className="flex flex-col items-center justify-center h-full p-6 bg-gray-50">
      <div className="w-full max-w-md bg-white rounded-xl shadow-md p-6">
        <h2 className="text-xl font-semibold mb-6 text-center truncate">{fileName}</h2>

        {isLoading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          </div>
        ) : error ? (
          <div className="text-red-500 p-3 text-center">{error}</div>
        ) : (
          <>
            <div className="mb-6">
              <audio
                ref={audioRef}
                src={file}
                onTimeUpdate={handleTimeUpdate}
                onLoadedData={handleLoadedData}
                onError={handleLoadError}
                onEnded={() => setIsPlaying(false)}
                className="hidden"
              />

              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-500">{formatTime(currentTime)}</span>
                <span className="text-sm text-gray-500">{formatTime(duration)}</span>
              </div>

              <input
                type="range"
                ref={progressBarRef}
                min="0"
                max={duration || 0}
                value={currentTime}
                onChange={handleProgressChange}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                style={{
                  background: `linear-gradient(to right, #3b82f6 ${(currentTime / duration) * 100}%, #e5e7eb ${(currentTime / duration) * 100}%)`,
                }}
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <button onClick={skipBackward} className="p-2 rounded-full hover:bg-gray-100">
                  <SkipBackIcon className="h-6 w-6 text-gray-700" />
                </button>

                <button
                  onClick={handlePlayPause}
                  className="p-3 bg-blue-500 rounded-full text-white hover:bg-blue-600 transition-colors"
                >
                  {isPlaying ? <PauseIcon className="h-6 w-6" /> : <PlayIcon className="h-6 w-6" />}
                </button>

                <button onClick={skipForward} className="p-2 rounded-full hover:bg-gray-100">
                  <SkipForwardIcon className="h-6 w-6 text-gray-700" />
                </button>
              </div>

              <div className="flex items-center space-x-2">
                <button onClick={toggleMute} className="p-2 rounded-full hover:bg-gray-100">
                  {isMuted ? (
                    <VolumeXIcon className="h-5 w-5 text-gray-700" />
                  ) : (
                    <Volume2Icon className="h-5 w-5 text-gray-700" />
                  )}
                </button>

                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="w-20 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  style={{
                    background: `linear-gradient(to right, #3b82f6 ${volume * 100}%, #e5e7eb ${volume * 100}%)`,
                  }}
                />
              </div>
            </div>

            <div className="mt-6 flex justify-center">
              <a
                href={file}
                download={fileName}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors flex items-center"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
                Download
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default AudioPlayer

